const { debounce } = window.BAO.Utils

class CartItem extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['remove']
  }

  constructor () {
    super()

    this._loading = false
    this.sections = this.inPushCart
      ? window.BAO.sectionRenderer.pushCartSections
      : this.onCartPage
        ? window.BAO.sectionRenderer.cartSections
        : []
  }

  setupListeners () {
    super.setupListeners()

    this.changeLineItem = this.changeLineItem.bind(this)
    this.removeLineItem = this.removeLineItem.bind(this)
    this.onQuantityChange = this.onQuantityChange.bind(this)
    this.debouncedOnQuantityChange = debounce((event) => {
      this.onQuantityChange(event)
    }, 500)

    if (this.els?.variant && this.els?.variant.exists) {
      this.listeners.add(
        this.els.variant.element,
        'change',
        this.changeLineItem,
      )
    }

    if (this.els?.remove.exists) {
      this.listeners.add(this.els.remove.element, 'click', this.removeLineItem)
    }

    this.listeners.add(
      this,
      window.BAO.EVENTS.QUANTITY_ADJUSTER.CHANGE,
      this.debouncedOnQuantityChange,
    )
  }

  /**
   * @param {MouseEvent} e
   *
   * @returns {void}
   */
  async removeLineItem (e, refresh = true) {
    e?.preventDefault()

    if (refresh && this.pushCart?.loading) return

    if (refresh) {
      this.loading = true
    }

    if (this.dataset.relatedKeys) {
      const splitRelatedKeys = this.dataset.relatedKeys.split(',')
      const key = this.dataset.key
      const allKeys = splitRelatedKeys.concat(key)

      const state = await window.BAO.Cart.removeItems(allKeys, {
        sections: this.mappedSections,
      })

      if (refresh) {
        this.loading = false
      }

      if (state && 'sections' in state) this.renderSections(state.sections)
    } else {
      const state = await window.BAO.Cart.removeItem(this.dataset.key, {
        sections: this.mappedSections,
      })

      if (refresh) {
        this.loading = false
        if (state && 'sections' in state) this.renderSections(state.sections)
      }
    }
  }

  async changeLineItem (event) {
    const select = event.target
    const variantId = select.value

    if (select.options[select.selectedIndex]?.disabled) {
      return
    }

    this.loading = true

    try {
      const properties = this.lineItemProperties
      if (properties == null) return

      const maxQuantity =
        select.options[select.selectedIndex]?.dataset?.maxInventory ?? 1
      const currentQuantity = select.dataset?.currentQuantity ?? 1
      const quantity = Math.min(currentQuantity, maxQuantity)

      await this.removeLineItem(null, false)

      const state = await window.BAO.Cart.addItem(
        { id: variantId, quantity, properties },
        {
          sections: this.mappedSections,
        },
      )

      if (state && 'sections' in state) this.renderSections(state.sections)
    } catch (error) {
      console.error('Error changing variant:', error)
    } finally {
      this.loading = false
    }
  }

  async onQuantityChange (e) {
    this.loading = true

    const state = await window.BAO.Cart.updateQuantity(
      this.dataset.key,
      e.detail.value,
      { sections: this.mappedSections },
    )

    this.loading = false

    if (!state) {
      this.handleCartErrorRender()

      return
    }

    if (state && 'sections' in state) this.renderSections(state.sections)
  }

  get mappedSections () {
    return this.sections.map((s) => s.section)
  }

  get lineItemProperties () {
    if (!this.dataset.properties) return {}

    try {
      return JSON.parse(this.dataset.properties)
    } catch (error) {
      console.error('Error parsing line item properties:', error)

      return null
    }
  }

  get loading () {
    return this._loading
  }

  set loading (val) {
    this._loading = val

    if (this.inPushCart) {
      this.pushCart.loading = val
    }

    this.setAttribute('aria-busy', val.toString())
  }

  get pushCart () {
    return this.closest('push-cart')
  }

  get inPushCart () {
    return this.pushCart != null
  }

  get cart () {
    return this.closest('cart-items')
  }

  get onCartPage () {
    return this.cart != null
  }
}

if (!customElements.get('cart-item')) {
  customElements.define('cart-item', CartItem)
}
